import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Code, Smartphone, Globe, Database, Cpu, Layers } from 'lucide-react';
import ConsultationModal from '../components/ConsultationModal';

const WHATSAPP_URL = 'https://wa.me/916366979916?text=Hi%20CloudXAI%2C%20I%20am%20interested%20in%20Software%20Development%20services';

const SoftwareDevelopmentPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} serviceType="Software Development" />
      
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)'}}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/" className="inline-flex items-center text-[#00E5FF] hover:text-[#1DE9B6] mb-8 transition-colors">← Back to Home</Link>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center shadow-xl shadow-[#00E5FF]/50">
              <Code className="w-8 h-8 text-[#0B0F1A]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white font-['Space_Grotesk']">Software Development</h1>
          </div>
          <div className="w-32 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mb-6"></div>
          <p className="text-xl text-[#9CA3AF] max-w-4xl">Custom software & application development services</p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-7xl mx-auto space-y-16">
          
          {/* Custom Software Development */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Code className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Custom Software Development</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We build tailored software solutions that align with your business workflows and goals. From planning to deployment, we ensure performance, scalability, and maintainability.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Key Offerings:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Enterprise applications</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Workflow automation systems</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Legacy system modernization</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Our Approach:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Agile methodology</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">CI/CD pipelines</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Test-driven development</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Web Application Development */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Globe className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Web Application Development</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We create dynamic, high-performance web applications with seamless user experiences, optimized for all screen sizes and browsers.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Technologies:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">React, Angular, Vue.js</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Node.js, Python, Java</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">RESTful APIs & GraphQL</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Solutions:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Customer portals</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">E-commerce platforms</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">SaaS applications</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Application Development */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Mobile Application Development</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We design and develop native and cross-platform mobile apps that enhance customer engagement and deliver business value.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Platforms:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">iOS (Swift, Objective-C)</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Android (Kotlin, Java)</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Cross-platform (Flutter, React Native)</span></div>
                </div>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Focus Areas:</h4>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Intuitive UI/UX design</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Performance optimization</span></div>
                  <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">App store deployment</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* API Development & Integration */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Database className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">API Development & Integration</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We build secure, scalable APIs that connect your systems, automate workflows, and enable seamless data exchange.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Services Include:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">RESTful API development</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Third-party integrations</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Payment gateway integration</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">API documentation & testing</span></div>
              </div>
            </div>
          </div>

          {/* AI & Automation Solutions */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Cpu className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">AI & Automation Solutions</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We integrate AI-powered features and automation tools into your applications to boost efficiency and enhance user experience.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">What We Offer:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">AI chatbots & virtual assistants</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Predictive analytics</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Process automation (RPA)</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Machine learning integration</span></div>
              </div>
            </div>
          </div>

          {/* Support & Maintenance */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Layers className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Support & Maintenance</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">We provide ongoing support and maintenance to ensure your software stays updated, secure, and high-performing.</p>
              </div>
            </div>
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Services Include:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Bug fixes & patches</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Performance optimization</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Feature enhancements</span></div>
                <div className="flex items-start space-x-2"><div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div><span className="text-[#9CA3AF]">Security updates</span></div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">Ready to Build Your Software Solution?</h2>
          <p className="text-xl text-[#9CA3AF] mb-8">Let's discuss your project requirements and create something amazing together</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setIsModalOpen(true)} className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300">Request Consultation</Button>
            <Button onClick={() => window.open(WHATSAPP_URL, '_blank')} className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent">Talk to Our Experts</Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SoftwareDevelopmentPage;
